/**
 * RenderBridge: keeps the render backend's proxies in sync with the world
 * (architecture §6.1). It is the only code that talks to the backend about
 * scene content.
 *
 * World and editor events only mark things dirty and request a frame. The real
 * work happens once per frame in `flush()` (the frame loop's "bridge" phase),
 * so a 60 Hz gizmo drag or a batch of commands costs one update per proxy per
 * frame, never one per event.
 *
 * Responsibilities:
 * - one proxy per (entity, rendering component), created/updated/destroyed via translators
 * - world transforms and effective (inherited) visibility
 * - re-translating dependents, e.g. a Canvas when its Text children or its zone's Role change (F-5),
 *   or a mesh when its entity's Animator changes
 * - selection → outlined proxies of the whole selected subtree (F-5)
 * - show flags, view mode, environment and post-process
 */
import type { Unsubscribe } from "../../core/Emitter";
import type { ReadableStore } from "../../core/store";
import { aabbTransform, aabbUnion } from "../../core/math/aabb";
import type { Aabb } from "../../core/math/aabb";
import type { Mat4 } from "../../core/math/mat4";
import { DEFAULT_ENVIRONMENT, DEFAULT_POST_PROCESS } from "../../render/proxies";
import type {
  EnvironmentDesc,
  PostProcessDesc,
  SkyDesc,
  FogDesc,
  ProxyDesc,
  ProxyHandle,
} from "../../render/proxies";
import type { RenderBackend } from "../../render/RenderBackend";
import { enabledComponent } from "../../world/entity";
import type { EntityId, EntityRecord } from "../../world/entity";
import { subtreeOf } from "../../world/hierarchy";
import type { TransformSystem } from "../../world/TransformSystem";
import type { WorldReader } from "../../world/World";
import type { SelectionState } from "../selection/Selection";
import type { SettingsValues } from "../settings/EditorSettings";
import { V1_TRANSLATORS } from "./translators";
import type { ComponentTranslator } from "./translators";

export interface RenderBridgeOptions {
  readonly world: WorldReader;
  readonly transforms: TransformSystem;
  readonly backend: RenderBackend;
  readonly selection: ReadableStore<SelectionState>;
  readonly settings: ReadableStore<SettingsValues>;
  /** Asks the frame loop for a frame; the bridge flushes in the "bridge" phase. */
  readonly requestFrame: (reason: string) => void;
  readonly translators?: readonly ComponentTranslator[];
  /** The camera the viewport is piloting: not drawn, since the view is from inside it. */
  readonly piloting?: ReadableStore<EntityId | null>;
  /**
   * True while a level loads. Models loaded in the background (MeshRenderer ›
   * Load in background) are withheld from the renderer until it's false, so
   * their download starts once the level is revealed, never alongside it.
   */
  readonly levelLoading?: ReadableStore<boolean>;
}

/** Level-wide components the renderer takes as settings rather than as proxies. */
const LEVEL_WIDE = new Set(["WorldSettings", "PostProcess", "Sky", "Fog"]);

/** One proxy per (entity, component) pair. */
type ProxyKey = `${EntityId}\u0000${string}`;

export class RenderBridge {
  private readonly world: WorldReader;
  private readonly transforms: TransformSystem;
  private readonly backend: RenderBackend;
  private readonly selection: ReadableStore<SelectionState>;
  private readonly settings: ReadableStore<SettingsValues>;
  private readonly requestFrame: (reason: string) => void;
  private readonly translators: ReadonlyMap<string, ComponentTranslator>;
  private readonly piloting: ReadableStore<EntityId | null> | null;
  private readonly levelLoading: ReadableStore<boolean> | null;

  private readonly handles = new Map<ProxyKey, ProxyHandle>();
  private readonly owners = new Map<ProxyHandle, EntityId>();
  /** The last description sent per proxy, for bounds without asking the backend. */

  // Dirty state, consumed by flush().
  private rebuild = true;
  private readonly dirtyProxies = new Set<ProxyKey>();
  private readonly dirtyVisibility = new Set<EntityId>();
  private selectionDirty = true;
  private viewDirty = true;
  private environmentDirty = true;

  private readonly unsubscribe: Unsubscribe[];

  constructor(options: RenderBridgeOptions) {
    this.world = options.world;
    this.transforms = options.transforms;
    this.backend = options.backend;
    this.selection = options.selection;
    this.settings = options.settings;
    this.requestFrame = options.requestFrame;
    this.piloting = options.piloting ?? null;
    this.levelLoading = options.levelLoading ?? null;
    this.translators = new Map(
      (options.translators ?? V1_TRANSLATORS).map((t) => [t.component, t]),
    );
    this.unsubscribe = this.listen();
  }

  /**
   * Bumped after every flush that changed what's drawn. DOM overlays (labels,
   * the selection bracket) subscribe to re-read bounds after the scene moved.
   */
  readonly revision: ReadableStore<number> = {
    subscribe: (listener) => {
      this.revisionListeners.add(listener);
      return () => {
        this.revisionListeners.delete(listener);
      };
    },
    getSnapshot: () => this.revisionCount,
  };
  private revisionCount = 0;
  private readonly revisionListeners = new Set<() => void>();

  /** Applies everything that changed since the last flush. Runs once per frame. */
  flush(): void {
    let changed = this.rebuild || this.dirtyProxies.size > 0 || this.dirtyVisibility.size > 0;
    changed ||= this.selectionDirty || this.viewDirty || this.environmentDirty;

    if (this.rebuild) this.rebuildAll();

    for (const key of this.dirtyProxies) this.syncProxy(key);
    this.dirtyProxies.clear();

    changed = this.pushTransforms() || changed;
    this.pushVisibility();
    if (this.selectionDirty) this.pushSelection();
    if (this.viewDirty) this.pushViewSettings();
    if (this.environmentDirty) this.pushEnvironment();

    if (changed) this.bumpRevision();
  }

  /** The proxies an entity owns (usually zero or one). */
  proxiesOf(id: EntityId): ProxyHandle[] {
    const record = this.world.get(id);
    if (!record) return [];
    return [...record.components.keys()]
      .map((type) => this.handles.get(proxyKey(id, type)))
      .filter((handle): handle is ProxyHandle => handle !== undefined);
  }

  /**
   * World-space bounds of everything drawn by these entities and their
   * descendants, or null if none of it has geometry. Feeds F-framing and the
   * selection bracket.
   *
   * Pending world changes are flushed first, so bounds are current even between
   * frames (framing a selection right after its level commits).
   */
  boundsOf(ids: readonly EntityId[]): Aabb | null {
    this.flush();
    return unionOf(ids.flatMap((root) => this.subtree(root)).map((id) => this.drawnBounds(id)));
  }

  /**
   * World-space bounds of what this entity itself draws, without its children,
   * or null if it draws nothing. Marquee selection tests each entity alone.
   */
  ownBoundsOf(id: EntityId): Aabb | null {
    this.flush();
    return this.drawnBounds(id);
  }

  private drawnBounds(id: EntityId): Aabb | null {
    // What's drawn, not what's described: a streamed model has bounds once it loads.
    const boxes = this.proxiesOf(id).map((handle) => {
      const local = this.backend.proxyBounds(handle);
      return local ? aabbTransform(local, this.transforms.worldMatrix(id)) : null;
    });
    return unionOf(boxes);
  }

  /** The entity under a canvas point, if any. */
  async pick(x: number, y: number): Promise<EntityId | null> {
    const handle = await this.backend.pick(x, y);
    return handle === null ? null : (this.owners.get(handle) ?? null);
  }

  dispose(): void {
    this.unsubscribe.forEach((off) => {
      off();
    });
    this.destroyAll();
  }

  // ─── Event intake ──────────────────────────────────────────────────────

  private listen(): Unsubscribe[] {
    const events = this.world.events;
    return [
      events.on("world:replaced", () => {
        this.rebuild = true;
        this.requestFrame("world replaced");
      }),
      events.on("component:changed", (event) => {
        this.onComponentChanged(event.id, event.type);
      }),
      events.on("entity:changed", (event) => {
        this.onEntityChanged(event.id, event.previous, event.next);
      }),
      this.selection.subscribe(() => {
        this.selectionDirty = true;
        this.requestFrame("selection");
      }),
      // A model arriving changes what's drawn without a world change: overlays re-read bounds.
      this.backend.onProxyBoundsChange(() => {
        this.bumpRevision();
      }),
      // Piloting hides one camera and shows the one it left.
      ...(this.piloting ? [this.followPiloting(this.piloting)] : []),
      ...(this.levelLoading ? [this.followLevelLoading(this.levelLoading)] : []),
      this.settings.subscribe(() => {
        this.viewDirty = true;
        // Game View hides the selection outline, so a view change re-pushes it.
        this.selectionDirty = true;
        this.requestFrame("view settings");
      }),
    ];
  }

  private onComponentChanged(id: EntityId, type: string): void {
    if (this.translators.has(type)) this.dirtyProxies.add(proxyKey(id, type));
    this.markSiblingReaders(id, type);
    const parent = this.world.get(id)?.parent;
    if (parent) this.markChildReaders(parent, type);
    this.markAncestorReaders(id);
    if (LEVEL_WIDE.has(type)) this.environmentDirty = true;

    // Transform changes are picked up from the TransformSystem during flush.
    this.requestFrame(`${type} changed`);
  }

  private onEntityChanged(
    id: EntityId,
    previous: EntityRecord | null,
    next: EntityRecord | null,
  ): void {
    if (previous?.visible !== next?.visible) {
      this.dirtyVisibility.add(id);
      // Hiding a post-process volume (or its parent) turns it off.
      this.environmentDirty = true;
    }
    // Turning a component off or on is, to everything drawn, removing or adding it.
    for (const type of toggledComponents(previous, next)) this.onComponentChanged(id, type);
    // A parent drawing its children (a Canvas) follows them coming, going, hiding or
    // being renamed, and its own children being reordered.
    for (const parent of new Set([previous?.parent, next?.parent])) {
      if (parent) this.markChildReaders(parent);
    }
    if (previous && next && previous.children !== next.children) this.markChildReaders(id);
    // A new parent changes inherited visibility and what parent-reading translators show.
    if (previous && next && previous.parent !== next.parent) {
      this.dirtyVisibility.add(id);
      for (const type of next.components.keys()) {
        const translator = this.translators.get(type);
        if (translator?.readsAncestors) {
          this.dirtyProxies.add(proxyKey(id, type));
        }
      }
      // Bound Text under it now resolves against different ancestors.
      this.markAncestorReaders(id);
    }
    this.requestFrame("entity changed");
  }

  /** Translators on the same entity that read `type` (a MeshRenderer reading its Animator) must re-translate. */
  private markSiblingReaders(id: EntityId, type: string): void {
    for (const component of this.world.get(id)?.components.keys() ?? []) {
      if (this.translators.get(component)?.readsSelf?.includes(type)) {
        this.dirtyProxies.add(proxyKey(id, component));
      }
    }
  }

  /**
   * A parent whose translator reads its children (a Canvas and its UI elements)
   * must re-translate: when one of them changes `type`, or (no type) the
   * children themselves change.
   */
  private markChildReaders(parentId: EntityId, type?: string): void {
    for (const component of this.world.get(parentId)?.components.keys() ?? []) {
      const reads = this.translators.get(component)?.readsChildren;
      if (reads && (type === undefined || reads.includes(type))) {
        this.dirtyProxies.add(proxyKey(parentId, component));
      }
    }
  }

  /** Descendants whose translators read their ancestors' content (bound Text) must re-translate. */
  private markAncestorReaders(ancestorId: EntityId): void {
    for (const id of this.subtree(ancestorId).slice(1)) {
      for (const component of this.world.get(id)?.components.keys() ?? []) {
        if (this.translators.get(component)?.readsAncestors) {
          this.dirtyProxies.add(proxyKey(id, component));
        }
      }
    }
  }

  // ─── Proxy lifecycle ───────────────────────────────────────────────────

  private rebuildAll(): void {
    this.destroyAll();
    for (const entity of this.world.getSnapshot().entities.values()) {
      for (const type of entity.components.keys()) {
        if (this.translators.has(type)) this.syncProxy(proxyKey(entity.id, type));
      }
    }

    this.rebuild = false;
    this.selectionDirty = true;
    this.environmentDirty = true;
  }

  /** Creates, updates or destroys the proxy for one (entity, component) to match the world. */
  /** A background model's description, without its asset while a level loads. */
  private withholdingBackground(desc: ProxyDesc): ProxyDesc {
    const held = desc.kind === "mesh" && desc.background && this.levelLoading?.getSnapshot();
    return held ? { ...desc, asset: null } : desc;
  }

  /** When a load starts or ends, mesh proxies re-sync, so background models are held or released. */
  private followLevelLoading(loading: ReadableStore<boolean>): Unsubscribe {
    return loading.subscribe(() => {
      for (const key of this.handles.keys()) {
        if (splitKey(key)[1] === "MeshRenderer") this.dirtyProxies.add(key);
      }
      this.requestFrame("level loading");
    });
  }

  private syncProxy(key: ProxyKey): void {
    const [id, type] = splitKey(key);
    const record = this.world.get(id);
    // Translators see only enabled components: a disabled one is absent (Unity's checkbox).
    const entity = record && withoutDisabled(record);
    const data = entity?.components.get(type);
    const translator = this.translators.get(type);
    const existing = this.handles.get(key);

    if (!entity || !data || !translator) {
      if (existing !== undefined) this.destroyProxy(key, existing);
      return;
    }

    const desc = this.withholdingBackground(translator.translate(entity, data, this.world));
    if (existing !== undefined) {
      this.backend.updateProxy(existing, desc);
      return;
    }

    const handle = this.backend.createProxy(desc);
    this.handles.set(key, handle);
    this.owners.set(handle, id);
    this.backend.setProxyTransform(handle, this.transforms.worldMatrix(id));
    this.backend.setProxyVisible(handle, this.isDrawn(id));
  }

  private destroyProxy(key: ProxyKey, handle: ProxyHandle): void {
    this.backend.destroyProxy(handle);
    this.handles.delete(key);
    this.owners.delete(handle);
  }

  private destroyAll(): void {
    for (const [key, handle] of this.handles) this.destroyProxy(key, handle);
  }

  // ─── Per-frame pushes ──────────────────────────────────────────────────

  /** Returns whether any entity moved. */
  private pushTransforms(): boolean {
    const moved = this.transforms.flush();
    for (const id of moved) {
      for (const handle of this.proxiesOf(id)) {
        this.backend.setProxyTransform(handle, this.transforms.worldMatrix(id));
      }
    }
    return moved.length > 0;
  }

  private bumpRevision(): void {
    this.revisionCount++;
    for (const listener of [...this.revisionListeners]) listener();
  }

  /** Visibility is inherited: hiding an entity hides its whole subtree. */
  private pushVisibility(): void {
    for (const id of this.dirtyVisibility) {
      for (const descendant of this.subtree(id)) {
        const visible = this.isDrawn(descendant);
        for (const handle of this.proxiesOf(descendant))
          this.backend.setProxyVisible(handle, visible);
      }
    }
    this.dirtyVisibility.clear();
  }

  private pushSelection(): void {
    // Game View shows the level as a player would: no selection outline.
    const shown = this.settings.getSnapshot().gameView ? [] : this.selection.getSnapshot().ids;
    const handles = shown.flatMap((id) => this.subtree(id).flatMap((e) => this.proxiesOf(e)));
    this.backend.setSelection(handles);
    this.selectionDirty = false;
  }

  private pushViewSettings(): void {
    const settings = this.settings.getSnapshot();
    // Game View drops the editor-only flags; wireframe is a way of drawing the level, so it stays.
    const editorFlags = !settings.gameView;
    this.backend.setShowFlags({
      grid: settings.showGrid && editorFlags,
      bounds: settings.showBounds && editorFlags,
      wireframe: settings.showWireframe,
      cameras: settings.showCameras && editorFlags,
      allFrustums: settings.showAllFrustums,
    });
    this.backend.setViewMode(settings.viewMode);
    this.viewDirty = false;
  }

  private pushEnvironment(): void {
    this.backend.setEnvironment(this.environment());
    this.backend.setPostProcess(this.postProcess());
    this.backend.setSky(this.sky());
    this.backend.setFog(this.fog());
    this.environmentDirty = false;
  }

  // ─── Lookups ───────────────────────────────────────────────────────────

  private environment(): EnvironmentDesc {
    const settings = this.firstComponent("WorldSettings");
    if (!settings) return DEFAULT_ENVIRONMENT;
    return {
      preset: settings.environment as EnvironmentDesc["preset"],
      floor: settings.floor as boolean,
    };
  }

  /** The first visible Sky, or none: a hidden sky is off, as a hidden volume is. */
  private sky(): SkyDesc | null {
    const sky = this.firstComponent("Sky", (id) => this.isVisible(id));
    if (!sky) return null;
    return {
      day: sky.day as string | null,
      night: sky.night as string | null,
      rotation: sky.rotation as number,
    };
  }

  /** The first visible Fog, or none: hidden fog is off. */
  private fog(): FogDesc | null {
    const fog = this.firstComponent("Fog", (id) => this.isVisible(id));
    if (!fog) return null;
    return {
      dayDensity: fog.dayDensity as number,
      nightDensity: fog.nightDensity as number,
      colorFromSky: fog.colorFromSky as boolean,
    };
  }

  /** The first visible volume's settings: a hidden volume is off, like any hidden entity. */
  private postProcess(): PostProcessDesc {
    const post = this.firstComponent("PostProcess", (id) => this.isVisible(id));
    if (!post) return DEFAULT_POST_PROCESS;
    return {
      exposure: post.exposure as number,
      bloomStrength: post.bloomStrength as number,
      bloomThreshold: post.bloomThreshold as number,
      tonemapper: post.tonemapper as PostProcessDesc["tonemapper"],
    };
  }

  /**
   * Level-wide singleton components (WorldSettings, PostProcess): the first
   * one found on an entity `include` accepts wins.
   */
  private firstComponent(type: string, include: (id: EntityId) => boolean = () => true) {
    for (const entity of this.world.getSnapshot().entities.values()) {
      const data = enabledComponent(entity, type);
      if (data && include(entity.id)) return data;
    }
    return undefined;
  }

  /** The entity and all its descendants, depth-first. */
  private subtree(id: EntityId): EntityId[] {
    return subtreeOf(this.world.getSnapshot().entities, id);
  }

  /** An entity's world matrix (for overlays anchored to entities without geometry). */
  worldMatrixOf(id: EntityId): Mat4 {
    return this.transforms.worldMatrix(id);
  }

  /** Whether its proxies draw: visible, and not the camera the viewport is looking out of. */
  private isDrawn(id: EntityId): boolean {
    return this.isVisible(id) && id !== this.piloting?.getSnapshot();
  }

  /** Re-pushes visibility for the camera that stopped being piloted and the one that started. */
  private followPiloting(piloting: ReadableStore<EntityId | null>): Unsubscribe {
    let previous = piloting.getSnapshot();
    return piloting.subscribe(() => {
      const next = piloting.getSnapshot();
      for (const id of [previous, next]) if (id !== null) this.dirtyVisibility.add(id);
      previous = next;
      this.requestFrame("piloting");
    });
  }

  /** Effective visibility: hidden if the entity or any ancestor is hidden. */
  isVisible(id: EntityId): boolean {
    for (
      let current = this.world.get(id);
      current;
      current = current.parent ? this.world.get(current.parent) : undefined
    ) {
      if (!current.visible) return false;
    }
    return true;
  }
}

function proxyKey(id: EntityId, component: string): ProxyKey {
  return `${id}\u0000${component}`;
}

function splitKey(key: ProxyKey): [EntityId, string] {
  const separator = key.indexOf("\u0000");
  return [key.slice(0, separator) as EntityId, key.slice(separator + 1)];
}

/** The union of the boxes that exist, or null if none do. */
function unionOf(boxes: readonly (Aabb | null)[]): Aabb | null {
  let union: Aabb | null = null;
  for (const box of boxes) {
    if (box) union = union ? aabbUnion(union, box) : box;
  }
  return union;
}

/** The record as translators see it: disabled components left out. */
function withoutDisabled(record: EntityRecord): EntityRecord {
  if (!record.disabled || record.disabled.size === 0) return record;
  const components = new Map(
    [...record.components].filter(([type]) => !record.disabled?.has(type)),
  );
  return { ...record, components };
}

/** Components whose enabled state differs between two versions of an entity. */
function toggledComponents(previous: EntityRecord | null, next: EntityRecord | null): string[] {
  if (!previous || !next || previous.disabled === next.disabled) return [];
  const before = previous.disabled ?? new Set<string>();
  const after = next.disabled ?? new Set<string>();
  return [...new Set([...before, ...after])].filter((type) => before.has(type) !== after.has(type));
}
